import { jsx, jsxs } from 'react/jsx-runtime';
import 'clsx';
import 'tailwind-merge';
import { Button } from '../page-layout/Button.js';
import '../ui/alert.js';
import '@radix-ui/react-avatar';
import { Badge } from '../ui/badge.js';
import { ArrowLeft, Save, X } from 'lucide-react';
import { forwardRef, useState, useRef, useImperativeHandle, useEffect, useMemo } from 'react';
import 'react-day-picker';
import '../ui/button.js';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../ui/card.js';
import '../ui/chart.js';
import '@radix-ui/react-checkbox';
import '@radix-ui/react-collapsible';
import 'cmdk';
import '@radix-ui/react-dialog';
import { DatePicker } from '../ui/date-picker.js';
import { DateTimePicker } from '../ui/datetime-picker.js';
import 'vaul';
import '@radix-ui/react-dropdown-menu';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import '@radix-ui/react-popover';
import '@radix-ui/react-progress';
import '../ui/radio-group.js';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select.js';
import '@radix-ui/react-separator';
import '../ui/sidebar.js';
import { Skeleton } from '../ui/skeleton.js';
import 'react-toastify';
import { Switch } from '../ui/switch.js';
import '@radix-ui/react-tabs';
import { Textarea } from '../ui/textarea.js';
import '@radix-ui/react-tooltip';
import './CrudDetailView.js';
import { Header, HeaderTitle, HeaderDescription } from '../page-layout/Header.js';
import { Breadcrumbs } from '../page-layout/Breadcrumbs.js';
import '../../contexts/AppContext.js';
import '../../contexts/OrganizationContext.js';
import '../../contexts/SidebarContext.js';
import '../../contexts/ThemeContext.js';
import { useApiClient } from '../../lib/api-client.js';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import 'react-router-dom';
import '../page-layout/FormPageLayout.js';
import '@dnd-kit/core';
import '@dnd-kit/sortable';
import '@dnd-kit/utilities';
import { ImageUploadDropzone } from '../form/ImageUploadDropzone.js';
import { RichTextEditor } from '../form/RichTextEditor.js';
import { toast } from '../../lib/toast.js';

function formatLastSaved(date) {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    if (diffSec < 60)
        return 'Last saved just now';
    if (diffMin < 60)
        return `Last saved ${diffMin}m ago`;
    if (diffHr < 24)
        return `Last saved ${diffHr}h ago`;
    return `Last saved ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
}
/**
 * Generic CRUD form view component for creating and editing items
 * @param config - CRUD configuration object defining fields and validation
 * @param itemId - ID of item to edit (undefined for create mode)
 * @param onBack - Optional callback when back button is clicked
 * @param onSuccess - Optional callback when form is successfully submitted
 * @param className - Additional CSS classes
 * @param breadcrumbs - Optional breadcrumb navigation items
 * @param headerActions - Optional React node rendered between header and form
 */
const CrudFormView = forwardRef(function CrudFormView({ config, itemId, onBack, onSuccess, className = '', breadcrumbs, headerActions, renderHeader = true, renderFooter = true, }, ref) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const isEdit = !!itemId;
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [tagInputs, setTagInputs] = useState({});
    const [lastSaved, setLastSaved] = useState(null);
    const [, setTick] = useState(0);
    // Programmatic submit (used by ref and form onSubmit)
    const submitFormRef = useRef(() => { });
    useImperativeHandle(ref, () => ({
        setFormData: (data) => {
            setFormData(prev => ({ ...prev, ...data }));
        },
        getFormData: () => formData,
        submit: () => submitFormRef.current(),
        get isPending() { return saveMutation?.isPending ?? false; },
        get lastSaved() { return lastSaved; },
    }), [formData, lastSaved]);
    // Refresh "last saved" display every 30s
    useEffect(() => {
        if (!lastSaved)
            return;
        const interval = setInterval(() => setTick(t => t + 1), 30000);
        return () => clearInterval(interval);
    }, [lastSaved]);
    // Get form fields from configuration sections
    const formFields = useMemo(() => {
        const sectionFields = new Set();
        // Collect all fields from form sections
        if (config.form.sections) {
            config.form.sections.forEach(section => {
                section.fields.forEach(fieldItem => {
                    const fieldKey = typeof fieldItem === 'object' ? fieldItem.field : fieldItem;
                    sectionFields.add(fieldKey);
                });
            });
        }
        // If no sections defined, include all non-readonly fields
        if (sectionFields.size === 0) {
            return config.fields.filter(field => !field.readonly);
        }
        // Return fields that are in the sections and not readonly
        return config.fields.filter(field => sectionFields.has(field.key) && !field.readonly);
    }, [config.fields, config.form.sections]);
    // Group fields by sections if configured
    const sections = useMemo(() => config.form.sections || [
        {
            title: 'Details',
            description: undefined,
            fields: formFields.map(f => f.key),
            columns: 1,
        },
    ], [config.form.sections, formFields]);
    // Fetch existing item data for edit mode
    const { data: response, isLoading } = useQuery({
        queryKey: [config.entityName, 'detail', itemId],
        queryFn: async () => {
            const result = await apiClient.get(config.endpoints.single(itemId));
            // Validate response format
            if (typeof result !== 'object' || result === null) {
                throw new Error('Invalid response format: expected JSON object');
            }
            if (!('success' in result) || !('data' in result)) {
                throw new Error('Invalid response structure: missing required fields (success, data)');
            }
            return result;
        },
        enabled: isEdit,
    });
    const existingItem = response?.success ? response.data : null;
    // Initialize form data
    useEffect(() => {
        const initialData = {};
        // Set default values from config
        formFields.forEach(field => {
            if (field.defaultValue !== undefined) {
                initialData[field.key] = field.defaultValue;
            }
        });
        // Override with existing data for edit mode
        if (existingItem) {
            formFields.forEach(field => {
                const value = existingItem[field.key];
                if (value !== undefined && value !== null) {
                    initialData[field.key] = value;
                }
            });
            // Initialize lastSaved from entity's updatedAt
            const updatedAt = existingItem.updatedAt;
            if (updatedAt) {
                setLastSaved(new Date(updatedAt));
            }
        }
        setFormData(initialData);
        setErrors({}); // Clear any existing errors when data changes
    }, [existingItem, formFields, itemId]); // Add itemId to deps to reset on mode change
    // Create/Update mutation
    const saveMutation = useMutation({
        mutationFn: async (data) => {
            if (isEdit) {
                return apiClient.patch(config.endpoints.update(itemId), data);
            }
            else {
                return apiClient.post(config.endpoints.create, data);
            }
        },
        onSuccess: response => {
            if (response.success) {
                toast.success(`${config.entityName} ${isEdit ? 'updated' : 'created'} successfully`);
                setLastSaved(new Date());
                // Invalidate list queries
                queryClient.invalidateQueries({ queryKey: [config.entityName] });
                onSuccess?.(response.data);
            }
            else {
                toast.error(response.error || `Failed to ${isEdit ? 'update' : 'create'} ${config.entityName}`);
            }
        },
        onError: error => {
            console.error('Form submission error:', error);
            toast.error(`Failed to ${isEdit ? 'update' : 'create'} ${config.entityName}`);
        },
    });
    /**
     * Handle form field changes
     */
    const handleFieldChange = (key, value) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                [key]: value,
            };
            // Call onFieldChange callback if defined
            const field = config.fields.find(f => f.key === key);
            if (field?.onFieldChange) {
                field.onFieldChange(value, (fieldKey, fieldValue) => {
                    newData[fieldKey] = fieldValue;
                }, newData);
            }
            return newData;
        });
        // Clear field error when user starts typing
        if (errors[key]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[key];
                return newErrors;
            });
        }
    };
    /**
     * Handle tag input for tags field type
     */
    const handleTagInput = (key, inputValue) => {
        setTagInputs(prev => ({
            ...prev,
            [key]: inputValue,
        }));
    };
    /**
     * Add tag to tags array
     */
    const addTag = (key) => {
        const input = tagInputs[key]?.trim();
        if (!input)
            return;
        const currentTags = formData[key] || [];
        if (!currentTags.includes(input)) {
            handleFieldChange(key, [...currentTags, input]);
            setTagInputs(prev => ({
                ...prev,
                [key]: '',
            }));
        }
    };
    /**
     * Remove tag from tags array
     */
    const removeTag = (key, tagToRemove) => {
        const currentTags = formData[key] || [];
        handleFieldChange(key, currentTags.filter((tag) => tag !== tagToRemove));
    };
    /**
     * Validate form data
     */
    const validateForm = () => {
        const newErrors = {};
        formFields.forEach(field => {
            const value = formData[field.key];
            const fieldKey = field.key;
            // Required validation
            if (field.required &&
                (!value || value === '' || (Array.isArray(value) && value.length === 0))) {
                newErrors[fieldKey] = `${field.label} is required`;
                return;
            }
            // Field-specific validation
            if (field.validation) {
                const { min, max, pattern, custom } = field.validation;
                if (value !== undefined && value !== '' && value !== null) {
                    // Min/Max validation
                    if (min !== undefined && (typeof value === 'string' ? value.length : value) < min) {
                        newErrors[fieldKey] =
                            `${field.label} must be at least ${min}${typeof value === 'string' ? ' characters' : ''}`;
                    }
                    if (max !== undefined && (typeof value === 'string' ? value.length : value) > max) {
                        newErrors[fieldKey] =
                            `${field.label} must be at most ${max}${typeof value === 'string' ? ' characters' : ''}`;
                    }
                    // Pattern validation
                    if (pattern && typeof value === 'string' && !pattern.test(value)) {
                        newErrors[fieldKey] = `${field.label} format is invalid`;
                    }
                    // Custom validation
                    if (custom) {
                        const customError = custom(value);
                        if (customError) {
                            newErrors[fieldKey] = customError;
                        }
                    }
                }
            }
            // Global validation from config
            const validationKey = field.key;
            const globalValidation = config.form.validation?.[validationKey];
            if (globalValidation?.custom) {
                const customError = globalValidation.custom(value, formData);
                if (customError) {
                    newErrors[fieldKey] = customError;
                }
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    /**
     * Core submit logic (shared by form onSubmit and programmatic ref.submit)
     */
    const doSubmit = () => {
        if (validateForm()) {
            // Convert form data types before sending to API
            const processedData = { ...formData };
            // Convert numeric fields to numbers and handle empty/undefined values
            formFields.forEach(field => {
                const key = field.key;
                const value = processedData[key];
                if (field.type === 'number' || field.type === 'decimal') {
                    if (value !== undefined && value !== '' && value !== null) {
                        processedData[key] = parseFloat(value);
                    }
                }
                // Convert empty strings to null for optional image/select fields
                // This is intentional - user explicitly cleared the field
                if (value === '' &&
                    !field.required &&
                    (field.type === 'image' || field.type === 'select')) {
                    processedData[key] = null;
                }
            });
            // Filter out undefined values to only send fields that are actually set
            // This prevents wiping data when fields are not included in the form
            const filteredData = Object.fromEntries(Object.entries(processedData).filter(([_, value]) => value !== undefined));
            saveMutation.mutate(filteredData);
        }
    };
    // Keep submitFormRef in sync for the imperative handle
    submitFormRef.current = doSubmit;
    /**
     * Handle form submission (from <form> onSubmit)
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        doSubmit();
    };
    /**
     * Render form field based on field type
     */
    const renderField = (field) => {
        const key = field.key;
        const value = formData[key];
        const error = errors[key];
        const hasError = !!error;
        // Check for custom inputFormat first
        if (field.inputFormat) {
            const customContent = field.inputFormat(value, (newValue) => handleFieldChange(key, newValue), field, formData);
            // If inputFormat returns null/undefined, skip rendering the entire field
            if (customContent === null || customContent === undefined) {
                return null;
            }
            return (jsxs("div", { className: "space-y-2", children: [jsxs(Label, { htmlFor: key, children: [field.label, field.required && jsx("span", { className: "text-destructive", children: " *" })] }), customContent, error && jsx("p", { className: "text-sm text-destructive", children: error }), field.helpText && jsx("p", { className: "text-sm text-muted-foreground", children: field.helpText })] }));
        }
        switch (field.type) {
            case 'boolean':
                return (jsxs("div", { className: "flex items-center space-x-2", children: [jsx(Switch, { id: key, checked: !!value, onCheckedChange: checked => handleFieldChange(key, checked) }), jsx(Label, { htmlFor: key, children: field.label })] }));
            case 'select':
                const selectValue = value ? String(value) : '';
                return (jsxs("div", { className: "space-y-2", children: [jsxs(Label, { htmlFor: key, children: [field.label, field.required && jsx("span", { className: "text-destructive", children: " *" })] }), jsxs(Select, { value: selectValue, onValueChange: val => handleFieldChange(key, val), children: [jsx(SelectTrigger, { className: hasError ? 'border-destructive' : '', children: jsx(SelectValue, { placeholder: field.placeholder || `Select ${field.label}` }) }), jsx(SelectContent, { children: field.options?.map(option => (jsx(SelectItem, { value: option.value, children: option.label }, option.value))) })] }, `${key}-${selectValue}`), error && jsx("p", { className: "text-sm text-destructive", children: error }), field.helpText && jsx("p", { className: "text-sm text-muted-foreground", children: field.helpText })] }));
            case 'textarea':
                return (jsxs("div", { className: "space-y-2", children: [jsxs(Label, { htmlFor: key, children: [field.label, field.required && jsx("span", { className: "text-destructive", children: " *" })] }), jsx(Textarea, { id: key, value: value || '', onChange: e => handleFieldChange(key, e.target.value), placeholder: field.placeholder, className: hasError ? 'border-destructive' : '' }), error && jsx("p", { className: "text-sm text-destructive", children: error }), field.helpText && jsx("p", { className: "text-sm text-muted-foreground", children: field.helpText })] }));
            case 'richtext':
                return (jsxs("div", { className: "space-y-2", children: [jsxs(Label, { htmlFor: key, children: [field.label, field.required && jsx("span", { className: "text-destructive", children: " *" })] }), jsx(RichTextEditor, { value: value || '', onChange: val => handleFieldChange(key, val), className: hasError ? 'border-destructive' : '' }), error && jsx("p", { className: "text-sm text-destructive", children: error }), field.helpText && jsx("p", { className: "text-sm text-muted-foreground", children: field.helpText })] }));
            case 'image':
                return (jsx(ImageUploadDropzone, { value: value || '', onChange: val => handleFieldChange(key, val), label: field.label, required: field.required, error: error, targetAspectRatio: field.targetAspectRatio }));
            case 'tags':
                return (jsxs("div", { className: "space-y-2", children: [jsxs(Label, { htmlFor: key, children: [field.label, field.required && jsx("span", { className: "text-destructive", children: " *" })] }), jsxs("div", { className: "space-y-2", children: [jsxs("div", { className: "flex gap-2", children: [jsx(Input, { placeholder: field.placeholder || 'Add a tag...', value: tagInputs[key] || '', onChange: e => handleTagInput(key, e.target.value), onKeyDown: e => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addTag(key);
                                                }
                                            }, className: hasError ? 'border-destructive' : '' }), jsx(Button, { type: "button", variant: "outline", onClick: () => addTag(key), disabled: !tagInputs[key]?.trim(), children: "Add" })] }), Array.isArray(value) && value.length > 0 && (jsx("div", { className: "flex flex-wrap gap-2", children: value.map((tag) => (jsxs(Badge, { variant: "secondary", className: "flex items-center gap-1", children: [tag, jsx("button", { type: "button", onClick: () => removeTag(key, tag), className: "ml-1 hover:text-destructive", children: jsx(X, { className: "h-3 w-3" }) })] }, tag))) }))] }), error && jsx("p", { className: "text-sm text-destructive", children: error }), field.helpText && jsx("p", { className: "text-sm text-muted-foreground", children: field.helpText })] }));
            case 'custom':
                // Custom field type - should have been handled by inputFormat above
                // This is a fallback in case inputFormat is not provided
                return (jsxs("div", { className: "space-y-2", children: [jsxs(Label, { htmlFor: key, children: [field.label, field.required && jsx("span", { className: "text-destructive", children: " *" })] }), jsx("div", { className: "text-sm text-muted-foreground", children: "Custom field type requires inputFormat function" }), error && jsx("p", { className: "text-sm text-destructive", children: error })] }));
            case 'date':
                return (jsxs("div", { className: "space-y-2", children: [jsxs(Label, { htmlFor: key, children: [field.label, field.required && jsx("span", { className: "text-destructive", children: " *" })] }), jsx(DatePicker, { date: value ? new Date(value) : undefined, onDateChange: date => handleFieldChange(key, date?.toISOString()), placeholder: field.placeholder || 'Select date', className: hasError ? 'border-destructive' : '' }), error && jsx("p", { className: "text-sm text-destructive", children: error }), field.helpText && jsx("p", { className: "text-sm text-muted-foreground", children: field.helpText })] }));
            case 'datetime':
                return (jsxs("div", { className: "space-y-2", children: [jsxs(Label, { htmlFor: key, children: [field.label, field.required && jsx("span", { className: "text-destructive", children: " *" })] }), jsx(DateTimePicker, { date: value ? new Date(value) : undefined, onDateTimeChange: date => handleFieldChange(key, date?.toISOString()), className: hasError ? 'border-destructive' : '' }), error && jsx("p", { className: "text-sm text-destructive", children: error }), field.helpText && jsx("p", { className: "text-sm text-muted-foreground", children: field.helpText })] }));
            default:
                // text, email, number, decimal
                const inputType = field.type === 'email'
                    ? 'email'
                    : field.type === 'number' || field.type === 'decimal'
                        ? 'number'
                        : 'text';
                return (jsxs("div", { className: "space-y-2", children: [jsxs(Label, { htmlFor: key, children: [field.label, field.required && jsx("span", { className: "text-destructive", children: " *" })] }), jsx(Input, { id: key, type: inputType, value: value || '', onChange: e => {
                                const val = field.type === 'number' || field.type === 'decimal'
                                    ? e.target.value
                                        ? parseFloat(e.target.value)
                                        : ''
                                    : e.target.value;
                                handleFieldChange(key, val);
                            }, placeholder: field.placeholder, className: hasError ? 'border-destructive' : '', step: field.type === 'decimal' ? '0.01' : undefined }), error && jsx("p", { className: "text-sm text-destructive", children: error }), field.helpText && jsx("p", { className: "text-sm text-muted-foreground", children: field.helpText })] }));
        }
    };
    if (isEdit && isLoading) {
        return (jsx("div", { className: className, children: jsxs("div", { className: "space-y-6", children: [renderHeader && (jsxs("div", { className: "space-y-4", children: [breadcrumbs && (jsxs("div", { className: "flex gap-2", children: [jsx(Skeleton, { className: "h-4 w-16" }), jsx(Skeleton, { className: "h-4 w-4" }), jsx(Skeleton, { className: "h-4 w-24" })] })), jsxs("div", { className: "space-y-2", children: [jsx(Skeleton, { className: "h-8 w-64" }), jsx(Skeleton, { className: "h-4 w-96" })] })] })), sections.map((section, idx) => (jsxs(Card, { children: [jsxs(CardHeader, { children: [jsx(Skeleton, { className: "h-6 w-48" }), jsx(Skeleton, { className: "h-4 w-72" })] }), jsx(CardContent, { children: jsx("div", { className: "grid grid-cols-1 gap-6", children: [1, 2, 3].map(i => (jsxs("div", { className: "space-y-2", children: [jsx(Skeleton, { className: "h-4 w-32" }), jsx(Skeleton, { className: "h-10 w-full" })] }, i))) }) })] }, idx))), renderFooter && (jsx("div", { className: "flex items-center justify-end gap-4", children: jsx(Skeleton, { className: "h-10 w-24" }) }))] }) }));
    }
    return (jsx("div", { className: className, children: jsxs("div", { className: "space-y-6", children: [renderHeader && (jsxs("div", { className: "space-y-4", children: [breadcrumbs && jsx(Breadcrumbs, { items: breadcrumbs }), onBack && !breadcrumbs && (jsx("div", { className: "flex justify-start", children: jsx(Button, { variant: "link", onClick: onBack, icon: ArrowLeft, className: "p-0 h-auto", children: "Back" }) })), jsxs("div", { className: "flex items-center justify-between", children: [jsxs(Header, { children: [jsx(HeaderTitle, { children: isEdit ? `Edit ${config.entityName}` : `Create ${config.entityName}` }), jsx(HeaderDescription, { children: isEdit
                                                ? `Update ${config.entityName.toLowerCase()} information`
                                                : `Add a new ${config.entityName.toLowerCase()}` })] }), headerActions && (jsx("div", { className: "flex items-center gap-2", children: headerActions }))] })] })), jsxs("form", { onSubmit: handleSubmit, className: "space-y-6", children: [sections.map(section => {
                            // Get fields in the exact order specified in section.fields
                            const sectionFields = section.fields
                                .map(fieldItem => {
                                const fieldKey = typeof fieldItem === 'object' ? fieldItem.field : fieldItem;
                                return formFields.find(field => field.key === fieldKey);
                            })
                                .filter(field => field !== undefined);
                            if (sectionFields.length === 0)
                                return null;
                            const sectionId = section.title?.toLowerCase().replace(/\s+/g, '-') || 'form-section';
                            const columns = section.columns || 1;
                            const gridClass = columns === 2 ? 'grid-cols-2' : columns === 3 ? 'grid-cols-3' : 'grid-cols-1';
                            return (jsxs(Card, { children: [jsxs(CardHeader, { children: [section.title && jsx(CardTitle, { children: section.title }), section.description && jsx(CardDescription, { children: section.description })] }), jsx(CardContent, { children: jsx("div", { className: `grid ${gridClass} gap-6`, style: { gridAutoFlow: 'row' }, children: sectionFields.map(field => {
                                                // Render the field first to check if it returns null
                                                const fieldContent = renderField(field);
                                                // Skip rendering wrapper if field returns null (conditional field not shown)
                                                if (fieldContent === null)
                                                    return null;
                                                // Find the field item in section.fields to get colspan/rowspan
                                                const fieldItem = section.fields.find(f => (typeof f === 'object' ? f.field : f) === field.key);
                                                const layout = typeof fieldItem === 'object' ? fieldItem : undefined;
                                                const style = layout
                                                    ? {
                                                        gridColumn: layout.colspan ? `span ${layout.colspan}` : undefined,
                                                        gridRow: layout.rowspan ? `span ${layout.rowspan}` : undefined,
                                                    }
                                                    : undefined;
                                                return (jsx("div", { style: style, children: fieldContent }, field.key));
                                            }) }) })] }, sectionId));
                        }), renderFooter && (jsxs("div", { className: "flex items-center justify-end gap-4", children: [lastSaved && (jsx("span", { className: "text-sm text-muted-foreground mr-auto", children: formatLastSaved(lastSaved) })), onBack && (jsx(Button, { type: "button", variant: "outline", onClick: onBack, children: "Cancel" })), jsxs(Button, { type: "submit", disabled: saveMutation.isPending, loading: saveMutation.isPending, icon: Save, children: [isEdit ? 'Update' : 'Create', " ", config.entityName] })] }))] })] }) }));
});

export { CrudFormView };
//# sourceMappingURL=CrudFormView.js.map
